import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { donationService, candidateService, fileUploadService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, toNepaliNumerals } from '../../utils/formatters';
import { DONATION_LIMITS, PAYMENT_METHODS } from '../../constants/config';

const PartyDonationModal = ({ isOpen, onClose, initialTargetType = 'PARTY', initialCandidateId = '' }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [amount, setAmount] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [targetType, setTargetType] = useState(initialTargetType);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState(initialCandidateId);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [voucherFile, setVoucherFile] = useState(null);
  const [showGoalReachedModal, setShowGoalReachedModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTargetType(initialTargetType);
      setSelectedCandidateId(initialCandidateId);
      fetchCandidates();
    }
  }, [isOpen, initialTargetType, initialCandidateId]);

  useEffect(() => {
    if (targetType === 'CANDIDATE' && selectedCandidateId && candidates.length > 0) {
      const candidate = candidates.find((c) => c.id === selectedCandidateId);
      if (candidate && candidate.goalAmount > 0 && Number(candidate.raisedAmount) >= Number(candidate.goalAmount)) {
        setShowGoalReachedModal(true);
      }
    }
  }, [selectedCandidateId, targetType, candidates]);

  const fetchCandidates = async () => {
    try {
      setLoadingCandidates(true);
      const response = await candidateService.getAllCandidates({ limit: 100 });
      setCandidates(response?.data?.items || []);
    } catch (err) {
      console.error('Error fetching candidates:', err);
    } finally {
      setLoadingCandidates(false);
    }
  };

  const quickAmounts = [500, 1000, 5000, 10000, 25000];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      setError('You must accept the Terms and Conditions to proceed');
      return;
    }

    // if (!isAuthenticated) {
    //   navigate('/login', { state: { from: '/', message: 'Please login to make a donation to the party' } });
    //   onClose();
    //   return;
    // }

    setError('');
    setSuccess('');

    const donationAmount = parseFloat(amount);

    // Validation
    if (!donationAmount || donationAmount < DONATION_LIMITS.MIN) {
      setError(`Minimum donation amount is ${formatCurrency(DONATION_LIMITS.MIN, lang)}`);
      return;
    }

    if (donationAmount > DONATION_LIMITS.MAX) {
      setError(`Maximum donation amount is ${formatCurrency(DONATION_LIMITS.MAX, lang)}`);
      return;
    }

    if (!selectedPaymentMethod) {
      setError('Please select a payment method');
      return;
    }

    setProcessing(true);

    try {
      // Validation for candidate
      if (targetType === 'CANDIDATE' && !selectedCandidateId) {
        setError('Please select a candidate');
        setProcessing(false);
        return;
      }

      const paymentLabel = PAYMENT_METHODS.find((p) => p.id === selectedPaymentMethod)?.label || selectedPaymentMethod;

      // Initialize donation with selected payment method
      const donationData = {
        amount: donationAmount,
        targetType: targetType,
        candidateId: targetType === 'CANDIDATE' ? selectedCandidateId : null,
        isAnonymous: isAnonymous,
        paymentMethod: selectedPaymentMethod,
        returnUrl: `${window.location.origin}/payment/verify`,
      };

      if (selectedPaymentMethod === 'VOUCHER') {
        if (!voucherFile) {
          setError('Please upload payment voucher');
          setProcessing(false);
          return;
        }
        try {
          const uploadRes = await fileUploadService.uploadFile(voucherFile);
          console.log('Voucher upload response:', uploadRes);

          const fileData = uploadRes.data || uploadRes;

          if (!fileData || (!fileData.filePath && !fileData.url && !fileData.key)) {
            throw new Error('Upload failed - no file path received');
          }

          donationData.voucherImage = fileData.filePath || fileData.key || fileData.url;
        } catch (uploadErr) {
          console.error('Voucher upload failed:', uploadErr);
          setError('Failed to upload voucher. Please try again.');
          setProcessing(false);
          return;
        }
      }

      const response = await donationService.initializeDonation(donationData);

      console.log('Party donation initialization response:', response);

      const data = response?.data || response;

      if (selectedPaymentMethod === 'VOUCHER' && data?.status === 'success') {
        const successMessage = data.message || 'Voucher submitted successfully. Waiting for admin approval.';
        setSuccess(successMessage);
        // Clear form
        setAmount('');
        setVoucherFile(null);
        setAcceptedTerms(false);
        setSelectedPaymentMethod('');
        setProcessing(false);

        // Close modal and navigate
        setTimeout(() => {
          onClose();
          const targetPath = '/donations';
          navigate(targetPath, { state: { message: successMessage } });
        }, 1500);
        return;
      }

      const paymentUrl = data?.payment_url || data?.data?.payment_url;
      const method = data?.method || 'GET';
      const formData = data?.form_data;

      if (paymentUrl) {
        setSuccess(`Redirecting to ${paymentLabel} for payment...`);
        if (method === 'POST' && formData && typeof formData === 'object') {
          // eSewa requires POST form submit (see developer.esewa.com.np)
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = paymentUrl;
          form.style.display = 'none';
          Object.entries(formData).forEach(([key, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = String(value ?? '');
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
        } else {
          window.location.href = paymentUrl;
        }
      } else {
        console.error('Payment URL not found in response:', response);
        setError(`Failed to get payment URL from ${paymentLabel}. Please contact support.`);
      }
    } catch (err) {
      console.error('Donation error:', err);
      setError(err.response?.data?.message || 'Failed to process donation. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  // Helper to get logo path for payment method
  const getPaymentLogo = (methodId) => {
    switch (methodId) {
      case 'ESEWA':
        return '/esewa_logo.webp';
      // case 'CONNECTIPS':
      //   return '/connect_ips_logo.webp';
      case 'KHALTI':
        return '/khalti_logo.webp';
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-xl lg:max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold text-white">
                {targetType === 'PARTY' ? 'Donate to RSP Party' : 'Support a Candidate'}
              </h3>
              <p className="text-primary-100 text-sm mt-1">Make a difference with your contribution</p>
            </div>
            <button
              onClick={() => {
                onClose();
                setError('');
                setAmount('');
                setSuccess('');
                setAcceptedTerms(false);
                setSelectedPaymentMethod('');
              }}
              className="text-white hover:text-gray-200 transition p-2 hover:bg-white/10 rounded-lg"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">

          {success && (
            <div className="mb-4 p-4 bg-green-50 border-2 border-green-200 text-green-700 rounded-xl text-sm flex items-center gap-3 shadow-sm">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">{success}</span>
            </div>
          )}

          {/* Support Selection */}
          <div className="mb-6">
            <label className="block text-base font-semibold text-gray-800 mb-3">
              Who would you like to support?
            </label>
            <div className="flex gap-3">
              <label className={`flex-1 flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all shadow-sm ${targetType === 'PARTY'
                ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-md'
                : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                }`}>
                <input
                  type="radio"
                  name="targetType"
                  value="PARTY"
                  checked={targetType === 'PARTY'}
                  onChange={(e) => {
                    setTargetType(e.target.value);
                    setSelectedCandidateId('');
                  }}
                  className="sr-only"
                />
                <div className="text-center">
                  <p className="font-bold text-base">RSP Party</p>
                </div>
              </label>
              <label className={`flex-1 flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all shadow-sm ${targetType === 'CANDIDATE'
                ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-md'
                : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                }`}>
                <input
                  type="radio"
                  name="targetType"
                  value="CANDIDATE"
                  checked={targetType === 'CANDIDATE'}
                  onChange={(e) => setTargetType(e.target.value)}
                  className="sr-only"
                />
                <div className="text-center">
                  <p className="font-bold text-base">A Candidate</p>
                </div>
              </label>
            </div>
          </div>

          {targetType === 'CANDIDATE' && (
            <div className="mb-6">
              <label className="block text-base font-semibold text-gray-800 mb-2">
                Select Candidate <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedCandidateId}
                onChange={(e) => setSelectedCandidateId(e.target.value)}
                required={targetType === 'CANDIDATE'}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition text-base"
                disabled={loadingCandidates}
              >
                <option value="">-- Choose a Candidate --</option>
                {candidates.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.name} ({candidate.constituency})
                  </option>
                ))}
              </select>
              {loadingCandidates && (
                <p className="mt-2 text-sm text-gray-500 flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading candidates...
                </p>
              )}
            </div>
          )}

          {targetType === 'PARTY' && (
            <div className="mb-6 p-5 bg-gradient-to-r from-primary-50 to-primary-100 border-2 border-primary-200 rounded-xl shadow-sm">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary-600 rounded-full flex items-center justify-center shadow-md">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-bold text-lg text-primary-900">Rastriya Swatantra Party</p>
                  <p className="text-sm text-primary-700 mt-0.5">Support the party's mission and vision</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-700 rounded-xl text-sm flex items-start gap-3 shadow-sm">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Quick Amount Buttons */}
            <div>
              <label className="block text-base font-semibold text-gray-800 mb-3">
                Quick Amount
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {quickAmounts.map((quickAmount) => {
                  const displayAmount = quickAmount >= 1000
                    ? `${lang === 'ne' ? toNepaliNumerals(quickAmount / 1000) : quickAmount / 1000}K`
                    : (lang === 'ne' ? toNepaliNumerals(quickAmount) : quickAmount);
                  return (
                    <button
                      key={quickAmount}
                      type="button"
                      onClick={() => setAmount(quickAmount.toString())}
                      className={`px-2 py-3 sm:px-4 border-2 rounded-xl text-sm font-semibold transition-all shadow-sm ${amount === quickAmount.toString()
                        ? 'bg-primary-600 text-white border-primary-600 shadow-md scale-105'
                        : 'border-gray-300 text-gray-700 hover:bg-primary-50 hover:border-primary-300 hover:shadow'
                        }`}
                    >
                      {displayAmount}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom Amount */}
            <div>
              <label className="block text-base font-semibold text-gray-800 mb-2">
                Donation Amount ({lang === 'ne' ? 'रु' : 'NPR'}) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">{lang === 'ne' ? 'रु' : 'NPR'}</span>
                <input
                  type="number"
                  required
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition text-lg font-medium"
                  placeholder="Enter amount"
                  min={DONATION_LIMITS.MIN}
                  max={DONATION_LIMITS.MAX}
                />
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Min: <span className="font-semibold">{formatCurrency(DONATION_LIMITS.MIN, lang)}</span> | Max: <span className="font-semibold">{formatCurrency(DONATION_LIMITS.MAX, lang)}</span>
              </p>
            </div>

            {/* Anonymous Option */}
            {/* <div className="flex items-center">
            <input
              type="checkbox"
              id="partyAnonymous"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="partyAnonymous" className="ml-2 block text-sm text-gray-900">
              Make this donation anonymous
            </label>
          </div> */}

            {/* Payment Method – Compact Cards */}
            <div className="mb-6">
              <label className="block text-base font-semibold text-gray-800 mb-3">
                {lang === 'ne' ? 'भुक्तानी विधि' : 'Payment Method'} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
                {PAYMENT_METHODS.map((method) => {
                  const logoPath = getPaymentLogo(method.id);
                  return (
                    <label
                      key={method.id}
                      className={`flex flex-col items-center justify-center p-3 border-2 rounded-xl cursor-pointer transition-all bg-white shadow-sm h-20 sm:h-24 ${selectedPaymentMethod === method.id
                        ? 'border-primary-600 ring-2 ring-primary-200 bg-primary-50 shadow-md scale-105'
                        : 'border-gray-200 hover:border-primary-300 hover:shadow-md'
                        }`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method.id}
                        checked={selectedPaymentMethod === method.id}
                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                        className="sr-only"
                      />
                      {logoPath ? (
                        <img
                          src={logoPath}
                          alt={method.label}
                          className="h-8 sm:h-10 object-contain"
                        />
                      ) : (
                        <div className="text-center">
                          {method.id === 'VOUCHER' && (
                            <svg className={`w-6 h-6 mx-auto mb-1 ${selectedPaymentMethod === method.id ? 'text-primary-600' : 'text-gray-500'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          )}
                          <span className={`text-xs sm:text-sm font-bold ${selectedPaymentMethod === method.id ? 'text-primary-700' : 'text-gray-600'}`}>
                            {method.label}
                          </span>
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            {selectedPaymentMethod === 'VOUCHER' && (
              <div className="mb-6 p-4 border border-gray-200 rounded-xl bg-gray-50">
                <h4 className="font-bold text-gray-800 mb-2">Bank Details</h4>
                <p className="text-sm text-gray-600 mb-4">Direct Bank Deposit / Transfer</p>

                <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-4">
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm text-sm space-y-1.5">
                    <div className="flex justify-between border-b pb-1 mb-1">
                      <span className="text-gray-500">Bank</span>
                      <span className="font-bold text-gray-900">Nepal Bank Ltd.</span>
                    </div>
                    <div className="flex justify-between border-b pb-1 mb-1">
                      <span className="text-gray-500">Account No.</span>
                      <span className="font-bold text-primary-700">28700107308118000001</span>
                    </div>
                    <div className="flex justify-between border-b pb-1 mb-1">
                      <span className="text-gray-500">Account Name</span>
                      <span className="font-bold text-gray-900 text-right">RASTRIYA SWATANTRA PARTY</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Branch</span>
                      <span className="font-bold text-gray-900">Nayabazar</span>
                    </div>
                  </div>

                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm flex flex-col items-center justify-center">
                    <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Scan to Pay via FonePay</p>
                    <div className="flex gap-4 items-center">
                      <img src="/fonepayqr.webp" alt="QR" className="h-50 w-50 object-contain border border-gray-200 rounded p-1" />
                      <img src="/fonePayFull.webp" alt="FonePay" className="h-50 object-contain" />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Upload Payment Voucher / Screenshot <span className="text-red-500">*</span>
                  </label>
                  <label className="flex flex-col items-center px-4 py-4 bg-white text-blue rounded-lg shadow-sm tracking-wide uppercase border border-blue cursor-pointer hover:bg-blue-50 hover:text-blue-600 transition border-dashed border-gray-400">
                    <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M16.88 9.1A4 4 0 0 1 16 17H5a5 5 0 0 1-1-9.9V7a3 3 0 0 1 4.52-2.59A4.98 4.98 0 0 1 17 8c5 5 5 16.88 9.1zM10 14a1 1 0 0 0 1-1V9h2l-3-3-3 3h2v4a1 1 0 0 0 1 1z" />
                    </svg>
                    <span className="mt-1 text-sm leading-normal text-gray-500">{voucherFile ? voucherFile.name : 'Select a file'}</span>
                    <input type='file' className="hidden" accept="image/*,application/pdf" onChange={(e) => setVoucherFile(e.target.files[0])} />
                  </label>
                </div>
              </div>
            )}

            {/* Payment info */}
            {selectedPaymentMethod && (
              <div className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <p className="text-sm text-blue-800 font-medium">
                    You will be redirected to <span className="font-bold">{PAYMENT_METHODS.find((p) => p.id === selectedPaymentMethod)?.label || selectedPaymentMethod}</span> for secure payment processing.
                  </p>
                </div>
              </div>
            )}

            {/* Terms and Conditions - moved to bottom */}
            <div className="flex items-start p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
              <div className="flex items-center h-5">
                <input
                  id="donationTerms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="donationTerms" className="text-gray-700 cursor-pointer">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-primary-600 hover:text-primary-700 font-semibold underline"
                  >
                    Terms and Conditions
                  </button>
                  <span className="text-red-500"> *</span>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  setError('');
                  setAmount('');
                  setSuccess('');
                  setAcceptedTerms(false);
                  setSelectedPaymentMethod('');
                }}
                className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-button hover:bg-gray-50 transition font-semibold text-base shadow-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing || !acceptedTerms || !selectedPaymentMethod}
                className={`flex-1 px-6 py-4 rounded-button transition font-bold text-base shadow-lg ${processing || !acceptedTerms || !selectedPaymentMethod
                  ? 'bg-gray-400 text-white cursor-not-allowed shadow'
                  : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 shadow-primary-500/50 hover:shadow-xl'
                  }`}
              >
                {processing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  selectedPaymentMethod === 'VOUCHER' ? 'Submit Voucher' : 'Proceed to Payment'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Goal Reached Modal */}
      {showGoalReachedModal && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black bg-opacity-70"></div>

            <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-2xl rounded-2xl border-t-8 border-primary-600">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 mb-4">
                  <svg className="h-10 w-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Goal Reached!</h3>
                <p className="text-gray-600 mb-6">
                  This candidate has already reached their fundraising goal. To maximize our impact, please consider donating to the <span className="font-bold text-primary-700">RSP Party</span> fund instead.
                </p>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => {
                      setTargetType('PARTY');
                      setSelectedCandidateId('');
                      setShowGoalReachedModal(false);
                    }}
                    className="w-full px-4 py-3 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 transition shadow-lg transform hover:-translate-y-0.5"
                  >
                    Donate to Party
                  </button>
                  <button
                    onClick={() => {
                      setShowGoalReachedModal(false);
                      // Optionally: setTargetType('PARTY'); setSelectedCandidateId(''); onClose(); if we want to force exit
                    }}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
      }

      {/* Terms and Conditions Modal */}
      {
        showTermsModal && (
          <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
              <div
                className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                onClick={() => setShowTermsModal(false)}
              ></div>

              <div className="relative inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    Donation Terms and Conditions
                  </h3>
                  <button
                    onClick={() => setShowTermsModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="max-h-96 overflow-y-auto pr-2 text-sm text-gray-700 space-y-4">
                  <h4 className="font-semibold text-gray-900">RSP Fundraiser - Donation Terms and Conditions</h4>
                  <p className="text-xs text-gray-500">Last Updated: January 2026</p>

                  <div className="space-y-3">
                    <section>
                      <h5 className="font-semibold text-gray-800">1. Introduction</h5>
                      <p>By making a donation through the RSP Fundraiser platform, you agree to support the democratic fundraising initiatives of Rastriya Swatantra Party (RSP), Nepal. These Terms and Conditions govern your donation transaction.</p>
                    </section>

                    <section>
                      <h5 className="font-semibold text-gray-800">2. Eligibility to Donate</h5>
                      <p>To make a donation, you must:</p>
                      <ul className="list-disc ml-5 mt-1 space-y-1">
                        <li>Be at least 18 years of age</li>
                        <li>Be a citizen of Nepal or have legal authority to make political donations</li>
                        <li>Provide accurate and truthful information</li>
                        <li>Not be prohibited by law from making political donations</li>
                        <li>Use your own funds and payment methods</li>
                      </ul>
                    </section>

                    <section>
                      <h5 className="font-semibold text-gray-800">3. Donation Policy</h5>
                      <p>All donations made through this platform are:</p>
                      <ul className="list-disc ml-5 mt-1 space-y-1">
                        <li>Voluntary contributions to support democratic processes</li>
                        <li>Non-refundable unless required by law</li>
                        <li>Subject to Election Commission of Nepal guidelines</li>
                        <li>Recorded and reported as per transparency requirements</li>
                        <li>Limited to {formatCurrency(DONATION_LIMITS.MAX, lang)} per transaction as per platform limits</li>
                      </ul>
                    </section>

                    <section>
                      <h5 className="font-semibold text-gray-800">4. Use of Donations</h5>
                      <p>Rastriya Swatantra Party commits to using donations for:</p>
                      <ul className="list-disc ml-5 mt-1 space-y-1">
                        <li>Campaign and election-related activities</li>
                        <li>Party operations and administration</li>
                        <li>Community outreach programs</li>
                        <li>Political advocacy and awareness initiatives</li>
                        <li>Support for party candidates as applicable</li>
                      </ul>
                    </section>

                    <section>
                      <h5 className="font-semibold text-gray-800">5. Transparency and Accountability</h5>
                      <p>Rastriya Swatantra Party is committed to:</p>
                      <ul className="list-disc ml-5 mt-1 space-y-1">
                        <li>Maintaining complete transparency in fund utilization</li>
                        <li>Publishing donation records as required by election laws</li>
                        <li>Providing receipts for all donations made</li>
                        <li>Compliance with all financial reporting requirements</li>
                      </ul>
                    </section>

                    <section>
                      <h5 className="font-semibold text-gray-800">6. Payment Processing</h5>
                      <p>Payment is processed through secure payment gateways (eSewa, ConnectIPS, Khalti):</p>
                      <ul className="list-disc ml-5 mt-1 space-y-1">
                        <li>Your payment information is handled securely by the selected gateway</li>
                        <li>RSP does not store your payment card or wallet details</li>
                        <li>Transaction fees may apply as per the gateway&apos;s policies</li>
                      </ul>
                    </section>

                    <section>
                      <h5 className="font-semibold text-gray-800">7. Legal Compliance</h5>
                      <p>All donations must comply with:</p>
                      <ul className="list-disc ml-5 mt-1 space-y-1">
                        <li>Political Party Act of Nepal</li>
                        <li>Election Commission regulations</li>
                        <li>Anti-money laundering laws</li>
                        <li>Any applicable tax regulations</li>
                      </ul>
                    </section>

                    <section>
                      <h5 className="font-semibold text-gray-800">8. Contact Information</h5>
                      <p>For any queries regarding donations, please contact:</p>
                      <p className="mt-1">
                        <strong>Rastriya Swatantra Party</strong><br />
                        Email: info@rsp.org.np<br />
                        Website: www.rsp.org.np
                      </p>
                    </section>
                  </div>

                  <p className="text-xs text-gray-500 mt-4 pt-4 border-t">
                    By clicking "I Agree" or proceeding with your donation, you acknowledge that you have read, understood, and agree to be bound by these Donation Terms and Conditions.
                  </p>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setShowTermsModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setAcceptedTerms(true);
                      setShowTermsModal(false);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition"
                  >
                    I Agree
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default PartyDonationModal;
